// growthos/warchest-frontend/react-app/src/pages/scenario-builder/utils/convertToActivity.ts
import { Theme } from "@mui/material";

import { Activity } from "@/Components/v2/organism/ActivityItems/types";
import { ActivityLogDTO } from "@/pages/scenario-builder/api/getActivityLog";
import { ActivityEventEnum, Status, StatusEnum } from "@/pages/scenario-builder/types";
import { sanitizeHtml } from "@/Utils/html-sanitization";

interface User {
  name: string;
  email: string;
  initials: string;
}

interface CommentEventPayload {
  comment: string;
}

interface EventWithCommentPayload {
  type: string;
  eventUuid: string;
  payload: CommentEventPayload;
  extra?: any;
}

export const getUserName = (user: User): string => {
  return user.name || user.email || "";
};

// Map backend event types to frontend event types
const mapEventType = (backendType: string): ActivityEventEnum | null => {
  switch (backendType) {
    case "created":
      return ActivityEventEnum.ScenarioCreated;
    case "completed":
    case "approved":
    case "archived":
    case "error":
      return ActivityEventEnum.ScenarioStatusUpdated;
    case "cloned":
      return ActivityEventEnum.ScenarioDuplicated;
    case "run":
      return ActivityEventEnum.ScenarioSubmitted;
    case "comment":
      return ActivityEventEnum.ScenarioCommentAdded;
    default:
      return null;
  }
};

// Map backend event types to status enums for proper color lookup
const mapEventToStatus = (eventType: string): StatusEnum => {
  switch (eventType) {
    case "approved":
      return StatusEnum.Approved;
    case "archived":
      return StatusEnum.Archived;
    case "completed":
      return StatusEnum.Completed;
    case "error":
      return StatusEnum.Error;
    default:
      return StatusEnum.Unknown;
  }
};

// Generate initials from name or email
const getInitials = (name: string | null, email: string): string => {
  if (name) {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }
  return email.charAt(0).toUpperCase();
};

const getStatusFromEventData = (eventType: string, data: any, theme: Theme) => {
  const statusEnum = mapEventToStatus(eventType);
  const statusStyle = Status.getStyle(statusEnum, theme);

  switch (eventType) {
    case "approved":
      return {
        status: "Approved",
        statusColor: statusStyle.color,
      };
    case "archived":
      return {
        status: "Archived",
        statusColor: statusStyle.color,
      };
    case "error":
      return {
        status: "Error",
        statusColor: statusStyle.color,
      };
    case "completed":
      return {
        status: "Completed",
        statusColor: statusStyle.color,
      };
    default:
      return {
        status: data?.status || "Updated",
        statusColor: data?.statusColor || theme.palette.primary.main,
      };
  }
};

export const convertToActivity = (
  activityLog: ActivityLogDTO,
  theme: Theme,
  scenarioName: string
): Activity | null => {
  const { event, createdBy, createdAt } = activityLog;

  // Map backend event type to frontend event type
  const frontendEventType = mapEventType(event.type);
  if (!frontendEventType) return null;

  // Create user object with initials
  const user: User = {
    name: createdBy.name || "",
    email: createdBy.email,
    initials: getInitials(createdBy.name, createdBy.email),
  };

  const baseActivity = {
    id: event.eventUuid,
    eventType: frontendEventType,
    user,
    createdAt,
  };

  switch (frontendEventType) {
    case ActivityEventEnum.ScenarioStatusUpdated: {
      const statusInfo = getStatusFromEventData(event.type, event.payload, theme);
      return {
        ...baseActivity,
        eventType: ActivityEventEnum.ScenarioStatusUpdated,
        data: statusInfo,
      };
    }

    case ActivityEventEnum.ScenarioCreated:
      return {
        ...baseActivity,
        eventType: ActivityEventEnum.ScenarioCreated,
        data: {
          scenarioName: scenarioName,
        },
      };

    case ActivityEventEnum.ScenarioDuplicated:
      return {
        ...baseActivity,
        eventType: ActivityEventEnum.ScenarioDuplicated,
        data: {
          scenarioName: scenarioName,
          sourceScenarioName: event.extra?.scenarioName || "",
        },
      };

    case ActivityEventEnum.ScenarioSubmitted:
      return {
        ...baseActivity,
        eventType: ActivityEventEnum.ScenarioSubmitted,
      };

    case ActivityEventEnum.ScenarioCommentAdded:
      return {
        ...baseActivity,
        eventType: ActivityEventEnum.ScenarioCommentAdded,
        data: {
          htmlContent: sanitizeHtml((event as EventWithCommentPayload).payload?.comment || ""),
        },
      };

    default:
      return null;
  }
};
